import React from 'react';
import { Card, CardContent, Typography, Chip, Avatar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import DoneIcon from '@material-ui/icons/Done';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState } from 'react';

const useStyles = makeStyles((theme) => ({
  messageContainer: {
    display: 'flex',
    marginBottom: theme.spacing(1.5),
    alignItems: 'flex-end',
    gap: theme.spacing(1),
  },
  messageContainerOwn: {
    justifyContent: 'flex-end',
  },
  messageBubbleOther: {
    marginRight: 'auto',
    maxWidth: '90%',
    minWidth: '90%',
    backgroundColor: '#f0f0f0',
    color: '#002D55',
  },
  messageBubbleOwn: {
    marginLeft: 'auto',
    maxWidth: '90%',
    minWidth: '90%',
    backgroundColor: '#002D55',
    color: '#fff',
  },
  messageContent: {
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(2),
    wordWrap: 'break-word',
  },
  messageInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  timestamp: {
    fontSize: '0.75rem',
    opacity: 0.7,
    marginTop: theme.spacing(0.5),
  },
  autoReplyBadge: {
    height: 20,
    fontSize: '0.65rem',
  },
  senderName: {
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  readIndicator: {
    fontSize: '0.75rem',
    marginLeft: theme.spacing(0.5),
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: '#002D55',
    fontWeight: 'bold',
    color: '#fff',
  },
}));

export default function MessageBubble({ message, isSender, senderName, senderInitials }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
  const [acknowledging, setAcknowledging] = useState(false);
  const bubbleClass = isSender ? classes.messageBubbleOwn : classes.messageBubbleOther;
  const containerClass = isSender ? classes.messageContainerOwn : '';
  const resolvedSenderName = senderName || message.sender_name || message.sender_email || 'Care Provider';
  const displayName = isSender ? 'You' : resolvedSenderName;

  const isCancellationNotice = !isSender
    && /has been cancelled/i.test(String(message.content || ''))
    && /reschedule/i.test(String(message.content || ''));

  const handleLinkClick = (event, url) => {
    let parsedUrl;

    try {
      parsedUrl = new URL(url);
    } catch (error) {
      return;
    }

    if (parsedUrl.pathname === '/my_appointments') {
      event.preventDefault();

      if (location.pathname === '/my_appointments') {
        window.dispatchEvent(new CustomEvent('patient:open-reschedule-dialog', {
          detail: {
            appointmentId: parsedUrl.searchParams.get('reschedule_appointment_id') || '',
          },
        }));
        return;
      }

      history.push({
        pathname: parsedUrl.pathname,
        search: parsedUrl.search,
        hash: parsedUrl.hash,
      });
    }
  };

  const renderLink = (url, label, key) => {
    let parsedUrl;

    try {
      parsedUrl = new URL(url);
    } catch (error) {
      parsedUrl = null;
    }

    const isAppointmentLink = parsedUrl && parsedUrl.pathname === '/my_appointments';

    return (
      <a
        key={key}
        href={url}
        onClick={(event) => handleLinkClick(event, url)}
        target={isAppointmentLink ? undefined : '_blank'}
        rel={isAppointmentLink ? undefined : 'noopener noreferrer'}
        style={{ color: isSender ? '#fff' : '#002D55', textDecoration: 'underline' }}
      >
        {label}
      </a>
    );
  };

  const renderLinkLabel = (url) => {
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.pathname === '/my_appointments') {
        return 'Click here to reschedule';
      }
    } catch (error) {
      return url;
    }

    return url;
  };

  const renderMessageContent = (content) => {
    const text = String(content || '');
    const markdownPattern = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(markdownPattern);

    return parts.map((part, index) => {
      if (!part) {
        return null;
      }

      if (index % 3 === 1 && parts[index + 1] && /^https?:\/\/\S+$/.test(parts[index + 1])) {
        return renderLink(parts[index + 1], part, `${part}-${parts[index + 1]}-${index}`);
      }

      if (index % 3 === 2 && /^https?:\/\/\S+$/.test(part)) {
        return null;
      }

      if (/^https?:\/\/\S+$/.test(part)) {
        return renderLink(part, renderLinkLabel(part), `${part}-${index}`);
      }

      return <React.Fragment key={`${index}-${part}`}>{part}</React.Fragment>;
    });
  };

  const handleAcknowledge = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!token || !message?.id || acknowledging) return;

    setAcknowledging(true);
    try {
      const response = await fetch(`${backendBaseUrl}/api/messaging/messages/${message.id}/read/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        window.dispatchEvent(new CustomEvent('chat:message-read'));
      }
    } catch (error) {
      console.error('[MessageBubble] Failed to acknowledge cancellation message:', error);
    } finally {
      setAcknowledging(false);
    }
  };

  const getReadStatus = () => {
    if (!isSender) return null;
    return message.is_read ? (
      <DoneAllIcon style={{ fontSize: '1rem', marginLeft: '4px' }} />
    ) : (
      <DoneIcon style={{ fontSize: '1rem', marginLeft: '4px' }} />
    );
  };

  return (
    <div className={`${classes.messageContainer} ${containerClass}`}>
      {!isSender && (
        <Avatar className={classes.avatar}>
          {senderInitials || 'U'}
        </Avatar>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isSender ? 'flex-end' : 'flex-start', width: '100%' }}>
        <div className={bubbleClass} style={{ borderRadius: isSender ? '12px 12px 0px 12px' : '0px 12px 12px 12px' }}>
          <div className={classes.messageContent}>
            {message.is_auto_reply && (
              <Chip
                label="Auto-Reply"
                size="small"
                className={classes.autoReplyBadge}
                style={{
                  backgroundColor: isSender ? '#E8F5E9' : '#C8E6C9',
                  color: '#2E7D32',
                  marginBottom: '8px',
                }}
              />
            )}
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', color: isSender ? '#fff' : '#002D55' }}>
              {renderMessageContent(message.content)}
            </Typography>
            {isCancellationNotice && !message.is_read && (
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-start' }}>
                <button
                  type="button"
                  onClick={handleAcknowledge}
                  disabled={acknowledging}
                  style={{
                    background: '#e9eef3',
                    color: '#002D55',
                    border: '1px solid #cdd7e3',
                    borderRadius: 999,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: acknowledging ? 'wait' : 'pointer',
                  }}
                >
                  {acknowledging ? 'Updating...' : 'I understand'}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={classes.timestamp} style={{ color: '#666666' }}>
          {displayName} • {moment(message.created_at).format('YYYY-MM-DD HH:mm')}
          {getReadStatus()}
        </div>
      </div>
    </div>
  );
}
